import React, { useState, useEffect, useCallback } from 'react';
import { FaFileExcel, FaPrint } from 'react-icons/fa';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { exportToExcel } from '../utils/exportToExcel';

const TABS = [
  { key: 'corporate', label: '🏢 Korporativ Şirkətlər' },
  { key: 'delivery', label: '🚴 Çatdırılma' },
  { key: 'cafes', label: '☕ Kafə Dövriyyəsi' },
];

const monthOptions = () => {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ value: `${d.getFullYear()}-${d.getMonth() + 1}`, label: d.toLocaleDateString('az-AZ', { month: 'long', year: 'numeric' }) });
  }
  return opts;
};

export default function AccountingPage() {
  const { getAdminToken } = useAuth();
  const [activeTab, setActiveTab] = useState('corporate');
  const [period, setPeriod] = useState(monthOptions()[0].value);

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>📊 Mühasibatlıq</h1>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Korporativ şirkətlər, çatdırılma və kafə dövriyyəsi üzrə maliyyə hesabatları</p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
            background: activeTab === t.key ? '#1a1a2e' : '#fff', color: activeTab === t.key ? '#fff' : '#333',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd' }}>
          {monthOptions().map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {activeTab === 'corporate' && <CorporateTab getAdminToken={getAdminToken} period={period} />}
      {activeTab === 'delivery' && <DeliveryTab getAdminToken={getAdminToken} period={period} />}
      {activeTab === 'cafes' && <CafesTab getAdminToken={getAdminToken} period={period} />}
    </div>
  );
}

function Toolbar({ onExport, onPrint }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
      <button onClick={onExport} style={btnStyle('#16a34a')}><FaFileExcel /> Excel</button>
      <button onClick={onPrint} style={btnStyle('#2563eb')}><FaPrint /> Çap / PDF</button>
    </div>
  );
}

function btnStyle(color) {
  return {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
    border: `1px solid ${color}`, background: '#fff', color, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', minWidth: 160 }}>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e', marginTop: 4 }}>{value}</div>
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 700, color: '#666', textAlign: 'left' };
const td = { padding: '12px 16px', fontSize: 13, color: '#333' };

// ── Tab 1: Korporativ Şirkətlər ───────────────────────────────────────────────
function CorporateTab({ getAdminToken, period }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, month] = period.split('-').map(Number);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.rpc('admin_get_corporate_cafe_reports', {
      p_admin_token: getAdminToken(), p_month: month, p_year: year,
    });
    setRows(data?.success ? data.rows : []);
    setLoading(false);
  }, [getAdminToken, month, year]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const paidAmount = rows.filter(r => r.status === 'paid').reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const pendingAmount = totalAmount - paidAmount;

  const exportRows = rows.map(r => ({
    Kafe: r.business_name || '—',
    Şirkət: r.company_name || '—',
    'Əməliyyat sayı': r.transaction_count,
    'Sistem məbləği': Number(r.total_amount).toFixed(2),
    'Kafenin dediyi': r.cafe_reported_amount != null ? Number(r.cafe_reported_amount).toFixed(2) : '—',
    Uyğun: r.cafe_reported_amount == null ? '—' : (Math.abs(r.cafe_reported_amount - r.total_amount) < 0.01 ? 'Bəli' : 'Xeyr'),
    Status: r.status === 'paid' ? 'Ödənilib' : 'Gözləyir',
  }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Cəmi dövriyyə" value={`${totalAmount.toFixed(2)} ₼`} />
        <StatCard label="Ödənilib" value={`${paidAmount.toFixed(2)} ₼`} />
        <StatCard label="Gözləyir" value={`${pendingAmount.toFixed(2)} ₼`} />
        <StatCard label="Hesabat sayı" value={rows.length} />
      </div>
      <Toolbar onExport={() => exportToExcel(exportRows, `korporativ_hesabat_${period}`)} onPrint={() => window.print()} />
      {loading ? <p>Yüklənir...</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
          <thead><tr style={{ background: '#f8f9fb' }}>
            <th style={th}>Kafe</th><th style={th}>Şirkət</th><th style={th}>Əməliyyat</th>
            <th style={th}>Sistem məbləği</th><th style={th}>Kafenin dediyi</th><th style={th}>Uyğun</th><th style={th}>Status</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const match = r.cafe_reported_amount == null ? null : Math.abs(r.cafe_reported_amount - r.total_amount) < 0.01;
              return (
                <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={td}>{r.business_name || '—'}</td>
                  <td style={td}>{r.company_name || '—'}</td>
                  <td style={td}>{r.transaction_count}</td>
                  <td style={td}>{Number(r.total_amount).toFixed(2)} ₼</td>
                  <td style={td}>{r.cafe_reported_amount != null ? `${Number(r.cafe_reported_amount).toFixed(2)} ₼` : '—'}</td>
                  <td style={td}>{match == null ? '—' : (match ? '✅' : '❌')}</td>
                  <td style={td}>{r.status === 'paid' ? '✅ Ödənilib' : '⏳ Gözləyir'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Tab 2: Çatdırılma ──────────────────────────────────────────────────────────
function DeliveryTab({ getAdminToken, period }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, month] = period.split('-').map(Number);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.rpc('admin_get_delivery_orders', {
      p_admin_token: getAdminToken(), p_status: 'delivered', p_month: month, p_year: year,
    });
    setOrders(data?.success ? data.rows : []);
    setLoading(false);
  }, [getAdminToken, month, year]);

  useEffect(() => { load(); }, [load]);

  const poolOrders = orders.filter(o => o.courier_type === 'platform_pool');
  const totalRevenue = orders.reduce((s, o) => s + Number(o.subtotal) + Number(o.delivery_fee), 0);
  const totalDeliveryFee = orders.reduce((s, o) => s + Number(o.delivery_fee), 0);
  const totalCommission = poolOrders.reduce((s, o) => s + Number(o.delivery_fee) * 0.15, 0);
  const cashTotal = orders.filter(o => o.payment_method === 'cash').reduce((s, o) => s + Number(o.subtotal) + Number(o.delivery_fee), 0);
  const walletTotal = orders.filter(o => o.payment_method === 'corporate_wallet').reduce((s, o) => s + Number(o.subtotal) + Number(o.delivery_fee), 0);

  const exportRows = orders.map(o => ({
    Tarix: new Date(o.delivered_at).toLocaleDateString('az-AZ'),
    Kafe: o.location_name || '—',
    Ünvan: o.delivery_address,
    Cəmi: (Number(o.subtotal) + Number(o.delivery_fee)).toFixed(2),
    'Çatdırılma haqqı': Number(o.delivery_fee).toFixed(2),
    Ödəniş: o.payment_method === 'corporate_wallet' ? 'Korporativ' : 'Nağd',
    'Kuryer tipi': o.courier_type === 'platform_pool' ? 'Hovuz' : 'Öz kuryer',
  }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Cəmi dövriyyə" value={`${totalRevenue.toFixed(2)} ₼`} />
        <StatCard label="Çatdırılma haqqı" value={`${totalDeliveryFee.toFixed(2)} ₼`} />
        <StatCard label="Hovuz komissiyası (~15%)" value={`${totalCommission.toFixed(2)} ₼`} />
        <StatCard label="Nağd / Korporativ" value={`${cashTotal.toFixed(0)} ₼ / ${walletTotal.toFixed(0)} ₼`} />
      </div>
      <Toolbar onExport={() => exportToExcel(exportRows, `catdirilma_hesabat_${period}`)} onPrint={() => window.print()} />
      {loading ? <p>Yüklənir...</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
          <thead><tr style={{ background: '#f8f9fb' }}>
            <th style={th}>Tarix</th><th style={th}>Kafe</th><th style={th}>Cəmi</th>
            <th style={th}>Çatdırılma haqqı</th><th style={th}>Ödəniş</th><th style={th}>Kuryer</th>
          </tr></thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={td}>{new Date(o.delivered_at).toLocaleDateString('az-AZ')}</td>
                <td style={td}>{o.location_name || '—'}</td>
                <td style={td}>{(Number(o.subtotal) + Number(o.delivery_fee)).toFixed(2)} ₼</td>
                <td style={td}>{Number(o.delivery_fee).toFixed(2)} ₼</td>
                <td style={td}>{o.payment_method === 'corporate_wallet' ? 'Korporativ' : 'Nağd'}</td>
                <td style={td}>{o.courier_type === 'platform_pool' ? 'Hovuz' : 'Öz kuryer'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Tab 3: Kafə Dövriyyəsi (revenue_logs) ──────────────────────────────────────
function CafesTab({ getAdminToken, period }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, month] = period.split('-').map(Number);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.rpc('admin_get_revenue_logs', {
      p_admin_token: getAdminToken(), p_month: month, p_year: year,
    });
    setLogs(data?.success ? data.rows : []);
    setLoading(false);
  }, [getAdminToken, month, year]);

  useEffect(() => { load(); }, [load]);

  const total = logs.reduce((s, l) => s + Number(l.amount || 0), 0);
  const paid = logs.filter(l => l.status === 'paid').reduce((s, l) => s + Number(l.amount || 0), 0);
  const pending = logs.filter(l => l.status === 'pending').reduce((s, l) => s + Number(l.amount || 0), 0);
  const overdue = logs.filter(l => l.status === 'overdue').reduce((s, l) => s + Number(l.amount || 0), 0);

  const markPaid = async (id) => {
    if (!window.confirm('Bu ödənişi "Ödənilib" kimi işarələyirsiniz?')) return;
    await supabase.rpc('admin_mark_revenue_paid', { p_admin_token: getAdminToken(), p_log_id: id });
    load();
  };

  const exportRows = logs.map(l => ({
    Tarix: new Date(l.created_at).toLocaleDateString('az-AZ'),
    Kafe: l.business_name || '—',
    Məbləğ: Number(l.amount).toFixed(2),
    Status: l.status,
  }));

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Cəmi" value={`${total.toFixed(2)} ₼`} />
        <StatCard label="Ödənilib" value={`${paid.toFixed(2)} ₼`} />
        <StatCard label="Gözləyir" value={`${pending.toFixed(2)} ₼`} />
        <StatCard label="Gecikmiş" value={`${overdue.toFixed(2)} ₼`} />
      </div>
      <Toolbar onExport={() => exportToExcel(exportRows, `kafe_dovriyyesi_${period}`)} onPrint={() => window.print()} />
      {loading ? <p>Yüklənir...</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
          <thead><tr style={{ background: '#f8f9fb' }}>
            <th style={th}>Tarix</th><th style={th}>Kafe</th><th style={th}>Məbləğ</th><th style={th}>Status</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={td}>{new Date(l.created_at).toLocaleDateString('az-AZ')}</td>
                <td style={td}>{l.business_name || '—'}</td>
                <td style={td}>{Number(l.amount).toFixed(2)} ₼</td>
                <td style={td}>
                  {l.status === 'paid' ? '✅ Ödənilib' : l.status === 'overdue' ? '🔴 Gecikmiş' : '🟡 Gözləyir'}
                </td>
                <td style={td}>
                  {l.status !== 'paid' && (
                    <button onClick={() => markPaid(l.id)} style={{
                      padding: '5px 10px', borderRadius: 6, border: '1px solid #16a34a', background: '#fff',
                      color: '#16a34a', fontSize: 11, cursor: 'pointer',
                    }}>Ödənilib et</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
